
import React, { useState, useMemo } from 'react';
import { fetcher } from 'api/client';
import { createDelegationRule, fetchDelegationRulesUnpopulated, updateDelegationRule, deleteDelegationRule } from 'api/actions/delegation-rules';
import { toast } from 'components/Shared/Toast';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import ReactTable from 'components/Shared/ReactTable';
import bootbox from 'components/Modals/Bootbox';
import { fetchMembersOnly, fetchGroupsOnly } from 'api/actions/collaboration';
import { Button } from 'reactstrap';

function DelegationRulesContainer() {

    const { t } = useTranslation();

    const queryClient = useQueryClient();
    // ACTIONS
    const onError = ({ message = 'GeneralError' } = {}) => {
        toast.error(t(`errors.${message}`))
    }

    const onSuccess = (invalidate = true) => {
        toast.success(t('success'))
        setshowCreate(false);
        if (invalidate) queryClient.invalidateQueries('delegation-rules');
    }

    const { data, isSuccess } = useQuery('delegation-rules', () => fetcher(fetchDelegationRulesUnpopulated()));

    const { data: members } = useQuery('fetch-simple-members', () => fetcher(fetchMembersOnly()));

    const { data: groups } = useQuery('fetch-simple-groups', () => fetcher(fetchGroupsOnly()));

    const { mutate: mutateCreateType } = useMutation((data) =>
        fetcher(createDelegationRule(data),), { onError, onSuccess });

    const { mutate: mutateDeleteType } = useMutation((typeId) =>
        fetcher(deleteDelegationRule(typeId),), { onError, onSuccess });

    const { mutate: mutateUpdateType } = useMutation(({ _id, data }) =>
        fetcher(updateDelegationRule(_id, data),), { onError, onSuccess });

    const columns = useMemo(() => [
        {
            Header: t('delegationrules.title'),
            columns: [
                {
                    Header: t('delegationrules.name'),
                    accessor: 'name',
                    type: 'input'
                },
                {
                    Header: t('delegationrules.user'),
                    accessor: 'user',
                    type: 'select',
                    options: members?.map((m) => ({ label: `${m.givenName} ${m.familyName}`, value: m._id })),
                    defaultOption: data?.user,
                    showBlank: true
                },
                {
                    Header: t('delegationrules.group'),
                    accessor: 'group',
                    type: 'select',
                    options: groups?.map((m) => ({ label: m.name, value: m._id })),
                    defaultOption: data?.group,
                    showBlank: true
                }
            ],
        }
    ], [data?.group, data?.user, groups, members, t]);

    // STATES
    const initialState = Object.freeze(columns[0].columns.reduce((acc, cur) => ({ ...acc, [cur.accessor]: cur.defaultOption || '' }), {}));
    const [showCreate, setshowCreate] = useState(false);
    const [newData, setnewData] = useState(initialState);

    const validate = () => {
        if (!newData.name) return toast.error(t('errors.MissingDelegationName'));
        if (!newData.user && !newData.group) return toast.error(t('errors.MissingGroupOrUser'));
        if (newData.user && newData.group) return toast.error(t('errors.OnlyGroupOrUser'));
    }
    // HANDLERS
    const handleUpdate = async (delegationId, columnId, value) => {
        const update = data.find((f) => f._id === delegationId)
        if (columnId === 'user') update.group = '';
        else if (columnId === 'group') update.user = '';
        mutateUpdateType({ _id: delegationId, data: { ...update, [columnId]: value } });
    }

    const handleDelete = async (typeId) => {
        const confirm = await bootbox.confirm(t('delegationrules.confirm_delete'));
        if (confirm) mutateDeleteType(typeId);
    }

    const handleCreate = async () => {
        if (validate()) return;
        mutateCreateType(newData);
        setnewData(initialState);
    }

    return (
        <div className="profile-users">
            <h5>
                <i className="fas fa-file"></i>  {t('delegationrules.title')}
            </h5>
            <p >
                {t('delegationrules.add')}
                {!showCreate && <Button className="float-right text-primary" size="sm" color="default" onClick={() => setshowCreate(true)} > <i className="fas fa-plus"></i> {t('delegationrules.addnew')}</Button>}
            </p>
            {isSuccess &&
                <ReactTable
                    className="reportTable"
                    columns={columns}
                    data={data}
                    handleUpdate={handleUpdate}
                    setnewData={setnewData}
                    handleDelete={handleDelete}
                    handleCreate={handleCreate}
                    newData={newData}
                    showCreate={showCreate}
                    setshowCreate={setshowCreate}
                    key="contractTypeTable"
                />
            }
        </div>
    )
}

export default DelegationRulesContainer;